/* eslint-disable complexity, sonarjs/cognitive-complexity, @next/next/no-img-element, react/forbid-component-props */
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import {
  CLICK_AND_COLLECT_DELIVERY_OPTION_TYPE,
  CYF_DEAL_TYPE,
} from '../../brands/default/config';
import { PAGE_TYPES } from '../../brands/default/constants';
import { BASKET_DEAL_PRODUCT } from '../../brands/default/content/components/checkout';
import {
  SOCIAL_CUES,
  MARKETING_CLAIMS_ENABLED,
} from '../../brands/default/features';
import { MARKETING_CLAIMS_DEF_LINK } from '../../brands/default/links';
import { isOdd } from '../../helpers/checkout';
import {
  getUrlFromDeal,
  getProductPurchaseCap,
  isExist,
  isBookingCalendarOnRedemptionDeal,
  isBookingCalendarOnRedemptionBasketGiftProduct,
} from '../../helpers/deals';
import { replaceImageServer } from '../../helpers/image';
import { makeUrlAbsolute } from '../../helpers/url';
import ThemeContext from '../../providers/ThemeProvider';
import Message from '../_generic/Message';
import VipExclusiveTag from '../deal/shared-components/VipExclusiveTag';
import FlightPair from '../deal/shared-components/booking-calendar/FlightPair';
import BasketDealProductAvailableDates from './BasketDealProductAvailableDates';
import BasketDealProductDates from './BasketDealProductDates';
import BasketDealProductDelete from './BasketDealProductDelete';
import BasketDealProductDelivery from './BasketDealProductDelivery';
import BasketDealProductDeposit from './BasketDealProductDeposit';
import BasketDealProductGifting from './BasketDealProductGifting';
import BasketDealProductMerchantAmount from './BasketDealProductMerchantAmount';
import BasketDealProductMeta from './BasketDealProductMeta';
import InfoBox from './InfoBox';
import PassengerDetails from './passengerdetails/PassengerDetails';

export default function BasketDealProduct({
  bt,
  isAuthenticated,
  giftWrap,
  deal,
  product,
  socialCue,
  calendarDeal,
  isRedeemPostage,
  remainingQuantity,
}) {
  const theme = useContext(ThemeContext);
  const image = replaceImageServer({
    dealId: deal.id,
    image: product.image || deal.image,
  });
  let title = product.title || deal.headline || 'Untitled';
  const [message, setMessage] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const isInvalidPricePerPersonDeal =
    deal.pricePerPerson && isOdd(product.quantity);
  const isBookingCalendarOnRedemption = isBookingCalendarOnRedemptionDeal(deal);
  const isBookingCalendarOnRedemptionGift =
    isBookingCalendarOnRedemptionBasketGiftProduct(deal, product);
  const isChannelManagerDeal = deal?.display?.dynamicTravelDeal;

  if (isBookingCalendarOnRedemption && deal?.category?.friendlyName)
    title = product.friendlyName;

  if (
    isBookingCalendarOnRedemptionGift &&
    deal?.category?.friendlyName &&
    product.gift
  )
    title = product.friendlyName;

  useEffect(() => {
    const userInfo = product.userDealInfo?.maxQuantityUserCanBuy;
    let purchaseCap = getProductPurchaseCap(deal);

    if (isAuthenticated && isExist(userInfo) && userInfo >= 0) {
      purchaseCap = Math.min(purchaseCap, userInfo);
    }

    if (
      purchaseCap === 0 ||
      product.quantity === 0 ||
      (isExist(remainingQuantity) && remainingQuantity < 0)
    ) {
      setMessage(
        purchaseCap === 0
          ? BASKET_DEAL_PRODUCT.dealBasketMaxProduct
          : BASKET_DEAL_PRODUCT.dealBasketOverlimitProduct,
      );
      setIsDisabled(true);
    } else {
      setMessage('');
      setIsDisabled(false);
    }
  }, [deal, product, isAuthenticated, remainingQuantity]);

  // gift packs are added to other products so do not get rendered. visibility added to hide vip subscription
  if (
    product.giftPack ||
    product.giftWrap ||
    (typeof deal.visibility === 'boolean' && !deal.visibility)
  ) {
    return null;
  }
  const nonStandardDelivery = false;

  const clickAndCollectDelivery =
    !isRedeemPostage &&
    product.deliveryOptionType !== null &&
    product.deliveryOptionType === CLICK_AND_COLLECT_DELIVERY_OPTION_TYPE;

  return (
    <>
      {message && (
        <div className="message">
          <Message html message={message} />
        </div>
      )}
      <div className="product">
        <Link
          href={makeUrlAbsolute(
            getUrlFromDeal({
              deal,
              location: PAGE_TYPES.CHECKOUT,
              originPath: deal?.shareUrl,
            }),
          )}
        >
          <a
            className="product__title-link product__title-link--mobile"
            rel="noreferrer noopener"
            target="_blank"
          >
            <h2 className="product__title">{title}</h2>
          </a>
        </Link>
        {calendarDeal && (
          <BasketDealProductDates
            className="dates--mobile"
            flightType={deal.dealType}
            isChannelManagerDeal={isChannelManagerDeal}
            product={product}
          />
        )}
        <div className="product__image-wrapper">
          <img
            alt={title}
            className={classNames({
              product__image: true,
              'product__image--disabled': isDisabled,
            })}
            src={image}
          />
          <VipExclusiveTag visible={product?.vipExclusive} />
        </div>
        <div className="product__details">
          <Link
            href={makeUrlAbsolute(
              getUrlFromDeal({
                deal,
                location: PAGE_TYPES.CHECKOUT,
                originPath: deal?.shareUrl,
              }),
            )}
          >
            <a
              className="product__title-link"
              rel="noreferrer noopener"
              target="_blank"
            >
              <h2 className="product__title">{title}</h2>
            </a>
          </Link>
          {(isBookingCalendarOnRedemption ||
            (isChannelManagerDeal && product.gift) ||
            (isBookingCalendarOnRedemptionGift && product.gift)) &&
            product?.availabilityDatesCount && (
              <>
                <BasketDealProductAvailableDates
                  numAvailableDates={product.availabilityDatesCount}
                />
              </>
            )}
          {!isDisabled && (
            <>
              <div className="product__extras">
                {deal.depositAvailable && !product.disabled && (
                  <BasketDealProductDeposit
                    bt={bt}
                    deal={deal}
                    product={product}
                  />
                )}
                {(isBookingCalendarOnRedemptionGift || isChannelManagerDeal) &&
                  product.gift && (
                    <Message
                      iconPosition="left"
                      message={BASKET_DEAL_PRODUCT.dateFlexibility}
                      showIcon
                    />
                  )}
                {calendarDeal && !product.giftClaim && (
                  <>
                    <BasketDealProductDates
                      flightType={deal.dealType}
                      isChannelManagerDeal={isChannelManagerDeal}
                      product={product}
                    />
                    {deal.dealType === 'INTERNATIONAL_TRAVEL' &&
                      product?.flights && (
                        <FlightPair flights={product.flights} isBasket />
                      )}
                  </>
                )}
                {deal.giftable &&
                  deal.giftingOptions &&
                  deal?.giftingOptions?.length > 0 &&
                  !product.giftClaim && (
                    <BasketDealProductGifting
                      bt={bt}
                      giftWrap={giftWrap}
                      giftingOptions={deal.giftingOptions}
                      isBookingCalendarOnRedemptionDeal={
                        deal.bookingCalendarOnRedemption === true
                      }
                      isBookingCalendarOnRedemptionGift={
                        isBookingCalendarOnRedemptionGift
                      }
                      isChannelManagerDeal={isChannelManagerDeal}
                      product={product}
                    />
                  )}
              </div>
              <InfoBox
                hasDiscount={product.displayDiscount}
                show={isInvalidPricePerPersonDeal}
              >
                {BASKET_DEAL_PRODUCT.basedOnSharing}
              </InfoBox>
              <BasketDealProductMeta
                bt={bt}
                deal={deal}
                giftWrap={giftWrap}
                isAuthenticated={isAuthenticated}
                isChannelManagerDeal={isChannelManagerDeal}
                product={product}
                remainingQuantity={remainingQuantity}
              />
              {!isRedeemPostage &&
                product.productDeliveryOptions &&
                product.productDeliveryOptions.length > 1 && (
                  <BasketDealProductDelivery
                    bt={bt}
                    deal={deal}
                    product={product}
                  />
                )}
              <BasketDealProductMerchantAmount deal={deal} product={product} />
              {SOCIAL_CUES && socialCue && (
                <>
                  <p className="product__social-cue">
                    <span className="product__social-cue-message">
                      {socialCue}
                    </span>
                    {MARKETING_CLAIMS_ENABLED && (
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        onClick={() => {
                          window.open(MARKETING_CLAIMS_DEF_LINK, '_blank');
                        }}
                        style={{
                          cursor: 'pointer',
                        }}
                      />
                    )}
                  </p>
                </>
              )}
              {(nonStandardDelivery || clickAndCollectDelivery) && (
                <Message
                  html
                  iconPosition="left"
                  message={
                    (nonStandardDelivery &&
                      BASKET_DEAL_PRODUCT.advisitoryDeliveryOption) ||
                    ''
                    // : WARNING_POSTAGE_OPTION
                  }
                  zIndex={0}
                />
              )}
              {deal.deliveryExcludedForPostCode && (
                <Message
                  html
                  iconPosition="left"
                  message={BASKET_DEAL_PRODUCT.excludedTerritoriesPostcode}
                  zIndex={0}
                />
              )}
            </>
          )}
          {isDisabled && (
            <BasketDealProductDelete
              bt={bt}
              giftWrap={giftWrap}
              product={product}
            />
          )}
        </div>
      </div>
      {!isDisabled && deal.dealType === CYF_DEAL_TYPE && (
        <div className="product product--passenger">
          <PassengerDetails dealId={deal.id} product={product} />
        </div>
      )}
      <style jsx>{`
        .message {
          margin: 15px 15px -15px 15px;
        }
        .product {
          display: flex;
          padding: 15px;
          border-bottom: 1px solid ${theme.colors.primary};
        }
        .product:last-child {
          border-bottom: none;
        }
        .product__image-wrapper {
          position: relative;
          min-width: 320px;
          width: 320px;
          max-height: 215px;
        }
        .product__image {
          width: 100%;
          align-self: flex-start;
        }
        .product__image--disabled {
          min-width: 160px;
          width: 160px;
          align-self: flex-start;
          opacity: 0.3;
        }
        .product__extras {
          flex-grow: 1;
          padding: 15px 0;
        }
        .product__details {
          padding-left: 15px;
          width: 100%;
        }
        .product__title-link--mobile {
          display: none;
          font-size: 16px;
          margin-bottom: 15px;
        }
        .product__title {
          font-size: 20px;
          font-weight: 500;
          color: #333333;
          text-decoration: none;
          margin-bottom: 0;
        }
        .product__title:hover {
          color: ${theme.colors.primary};
          text-decoration: underline;
        }
        .product__meta {
          display: flex;
          justify-content: flex-end;
          width: 100%;
        }
        .product__social-cue {
          color: #d0021b;
          font-weight: bold;
          margin: 5px 0 0 0;
          text-align: right;
        }
        .product__social-cue-message {
          margin-right: 1%;
        }
        .product--passenger {
          display: block;
          padding: 0;
        }
        .product--passenger :global(.tile) {
          border-radius: 0;
        }
        @media (max-width: ${theme.breakpoints.lgDown}) {
          .product__title {
            font-size: 18px;
          }
          :global(.product .meta--now),
          :global(.product .meta--deposit) {
            display: none;
          }
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .product {
            flex-direction: column;
          }
          .product__image {
            width: 100%;
          }
          .product__details {
            padding-left: 0;
          }
          .product__title-link {
            display: none;
          }
          .product__title-link--mobile {
            display: block;
          }
          .product__social-cue {
            text-align: center;
            padding-bottom: 10px;
          }
        }
      `}</style>
    </>
  );
}

BasketDealProduct.propTypes = {
  bt: PropTypes.string,
  calendarDeal: PropTypes.bool,
  deal: PropTypes.object,
  giftWrap: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  isRedeemPostage: PropTypes.bool,
  product: PropTypes.object,
  remainingQuantity: PropTypes.number,
  socialCue: PropTypes.string,
};
